import React, { useState } from 'react';
import ProgramStats from '../components/programs/ProgramStats';
import { usePrograms, useUpdateProgram, useToggleVisibility } from '../hooks/usePrograms';
import ProgramCard from '../components/programs/ProgramCard';
import ProgramForm from '../components/programs/ProgramForm';
import ProgramDetailModal from '../components/programs/ProgramDetailModal';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Program, UpdateProgramData } from '../services/api';

const Dashboard: React.FC = () => {
  const { data: programs, isLoading } = usePrograms(true); // Only active programs
  const updateProgram = useUpdateProgram();
  const toggleVisibility = useToggleVisibility();

  // Modal states
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProgramForDetail, setSelectedProgramForDetail] = useState<Program | null>(null);

  const recentPrograms = programs?.slice(0, 6) || [];

  // Handler functions
  const handleEdit = (programId: string) => {
    const program = programs?.find(p => p._id === programId);
    if (program) {
      setSelectedProgram(program);
      setIsFormOpen(true);
    }
  };

  const handleViewDetails = (program: Program) => {
    setSelectedProgramForDetail(program);
    setIsDetailModalOpen(true);
  };

  const handleToggleVisibility = (programId: string) => {
    toggleVisibility.mutate(programId);
  };

  const handleSaveProgram = (data: UpdateProgramData) => {
    if (selectedProgram) {
      updateProgram.mutate(
        { id: selectedProgram._id, data },
        {
          onSuccess: () => {
            setIsFormOpen(false);
            setSelectedProgram(null);
          },
        }
      );
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedProgram(null);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProgramForDetail(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">Welcome to your affiliate program management panel</p>
      </div>

      {/* Stats */}
      <ProgramStats />

      {/* Recent Programs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <ClockIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Programs</h2>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-3"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : recentPrograms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentPrograms.map((program) => (
              <ProgramCard
                key={program._id}
                program={program}
                onEdit={handleEdit}
                onToggleVisibility={handleToggleVisibility}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 dark:text-gray-500 mb-3">
              <ClockIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No programs yet</h3>
            <p className="text-gray-600 dark:text-gray-400">Start by syncing your affiliate programs</p>
          </div>
        )}
      </div>

      {/* Program Form Modal */}
      <ProgramForm
        program={selectedProgram}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSave={handleSaveProgram}
        isLoading={updateProgram.isPending}
      />

      {/* Program Detail Modal */}
      <ProgramDetailModal
        program={selectedProgramForDetail}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        onEdit={handleEdit}
        onToggleVisibility={handleToggleVisibility}
      />
    </div>
  );
};

export default Dashboard; 